import React, { useCallback, useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import cn from 'classnames';
import useAuth from '../../hooks/useAuth';
import { useUserRegisterMutation, useUserOAuthLoginMutation } from '../../redux/slices/authSlice';

// Components
import RegisterForm from './RegisterForm';
import Meta from '../shared/Meta';

import styles from '../../../stylesheets/public/layouts/Register.module.postcss';

function Register() {
  const history = useHistory();

  const [queryError, setQueryError] = useState('');

  const [userRegister, { isError: isRegisterError, error: registerError }] = useUserRegisterMutation();
  const [userOAuthLogin, { isError: isOAuthError, error: oAuthError }] = useUserOAuthLoginMutation();
  const isError = isRegisterError || isOAuthError;
  const error = registerError || oAuthError;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorMsg = params.get('error');
    if (errorMsg) setQueryError(decodeURIComponent(errorMsg));
  }, []);

  const onFormSubmit = useCallback(async (model) => {
    const data = { ...model, catering_user: false };
    userRegister(data).unwrap().then(() => {
      history.push('/vendor/truck/new');
    });
  }, [userRegister, history]);

  const validateForm = (values) => {
    const { password, password_confirmation } = values;
    const errors = {};
    if (password_confirmation && password_confirmation !== password) {
      errors.password_confirmation = 'Passwords must match';
    }
    return errors;
  };

  return (
    <div className={cn(styles.Register, 'u-blockCenter u-limitWidth35 u-mb3 u-mt3')}>
      <Meta noindex />
      <h1 className='u-mb3 u-textCenter'>Create your account</h1>
      <div className='Box Box--white'>
        <div>
          { (isError || queryError) &&
            <div className={styles.FormMessages}>
              <span className='icon icon-warning' />
              <h4>
                { error?.data?.reason || queryError || 'There was an error. Please try again.' }
              </h4>
            </div>
          }

          <div className='u-mb1'>
            <RegisterForm
              validate={validateForm}
              onSubmit={onFormSubmit}
              initialValues={{ catering_user: false }}
            />
          </div>
          <hr className={styles.OrRule} />
          <div className='u-mb1'>
            <div className='u-text-sm u-blockCenter u-textCenter u-mr3 u-ml3 u-mb1'>
              By clicking Sign Up with Facebook or Sign Up with Google, you agree to the <Link to='/terms-and-conditions'>Terms of Use</Link> and <Link to='/privacy-policy'>Privacy Policy</Link>.
            </div>
            <button
              type='button'
              className={cn('Button Button--outlined u-w100', styles.Button__facebook)}
              onClick={() => {
                userOAuthLogin({
                  provider: 'facebook',
                  params: { needs_user_type: false, catering_user: false },
                  history
                });
              }}
            >
              <img src='/static/images/icons/facebook-logo-secondary.png' alt='Sign up with Facebook'/>
              <span>Continue with Facebook</span>
            </button>

            <button
              type='button'
              className={cn('Button Button--outlined u-w100', styles.Button__google, 'u-ml0 u-mt1')}
              onClick={() => {
                userOAuthLogin({
                  provider: 'google',
                  params: { needs_user_type: false, catering_user: false }
                });
              }}
            >
              <img src='/static/images/icons/google.svg' alt='Sign up with Google' />
              <span>Sign up with Google</span>
            </button>

            <button
              type='button'
              className={cn('Button u-w100 u-mt1', styles.Button__apple)}
              onClick={() => {
                userOAuthLogin({
                  provider: 'apple',
                  params: { needs_user_type: false, catering_user: false }
                });
              }}
            >
              <img src='/static/images/appleid_button_signup.png' height='48' alt='Sign up with Apple'/>
            </button>
          </div>
        </div>
      </div>
      <div className='u-mt2'>
        <small>Already have an account? <Link to='/login'>Log in</Link>.</small>
      </div>
    </div>
  );
}

export default Register;
